import { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import ManagementUploadFile from "../Managements/ManagementsUploadFile/ManagementsUploadFile";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { useTranslation } from "react-i18next";
import { ChangeOwnershipData } from "../../core/models/ownerData/ownerData.model";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import ManagementHeader from "../ManagementsUpdate/ManagementsUpdateHeader/ManagementsUpdateHeader";
import ManagementsRequestState from "../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  ManagementRequestInfo,
  ManagementsSuccessfulRequests,
  PostCallCenterRequestBody,
  RequestStateDescriptionData,
} from "../../core/models/managements/managements.model";
import {
  formatOwnerDataCup,
  getManagementsRequestTypeData,
  initialManagementRequestInfo,
  initialRequestStateData,
} from "../../utils/components/managements/managements.util";
import CupsCifSelector from "../CupsCifSelector/CupsCifSelector";
import { ManagementUpdateCup } from "../../core/models/cupsCifSelector/cupsCifSelector.model";
import theme from "../../muiTheme";
import { ManagementUpdateOwnershipData } from "../../core/models/managementsUpdate/managementsUpdateOwnership/managementsUpdateOwnership";
import {
  MANAGEMENTS_ENVIRONMENT_GAS,
  MANAGEMENTS_ENVIRONMENT_LIGHT,
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_SUBTYPE_OWNERSHIP_GAS,
  MANAGEMENTS_SUBTYPE_OWNERSHIP_LIGHT,
  MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
} from "../../core/constants/constants";
import {
  postCallCenter,
  postImportDocument,
} from "../../core/services/managements/managements.service";
import { formatDate } from "../../utils/dateUtils/formatDate.util";
import ManagementsCancelProcess from "../Managements/ManagementsCancelProcess/ManagementsCancelProcess";
import { useSearchParams } from "react-router-dom";
import { getSelectedCups } from "../../utils/components/cupsCifSelector/cupsCifSelector.util";
import { getRecipientId } from "../../utils/components/services/services.util";

const ManagementOwnershipChange = () => {
  const { t } = useTranslation();
  const {
    ownerData,
    userData,
    isAgent,
    cifCupsState: { listCupsCifFiltered },
  } = useContext(TotalEnergiesContext);
  const [searchParms] = useSearchParams();
  const idCups = searchParms.get("idCups") || "";
  const [step, setStep] = useState<number>(-1);
  const [selectedCups, setSelectedCups] = useState<ManagementUpdateCup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>(initialRequestStateData);
  const [managementRequestInfo, setManagementRequestInfo] = useState<
    ManagementRequestInfo[] | null
  >([initialManagementRequestInfo]);
  const [documentImportErrors, setDocumentImportErrors] = useState<
    Record<string, string[]>
  >({});
  const [updateOwnershipData, setUpdateOwnershipData] =
    useState<ManagementUpdateOwnershipData>({
      idClient: "",
      isAdministrator: "",
      cups: [],
    });
  const ownerDataCup =
    ownerData?.cups.find(
      ({ idCups: ownerDataIdCups }) => ownerDataIdCups.toString() === idCups,
    ) || initialOwnerDataCup;

  const updateOwnershipFields = [
    {
      id: "social",
      label: "Razón Social",
      inputMode: "text",
    },
    {
      id: "fiscal",
      label: "Dirección Fiscal",
      inputMode: "text",
    },
  ];

  const documentTypeOptions = [
    {
      id: "NIF/CIF",
      label: "NIF/CIF",
    },
    {
      id: "NIE",
      label: "NIE",
    },
    {
      id: "Passport",
      label: "Pasaporte",
    },
    {
      id: "Other",
      label: "Otro",
    },
  ];

  const [changeText, setChangeText] = useState("");
  const [formData, setFormData] = useState<ChangeOwnershipData>({
    document: "",
    social: "",
    fiscal: "",
  });

  const [documentType, setDocumentType] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [documentInputEnabled, setDocumentInputEnabled] = useState(true);

  const validateDocument = (type: string, value: string) => {
    let isValid = true;
    let errorMessage = "";
    const nifRegex = /^(\d{8})([a-zA-Z])$/;
    const cifRegex =
      /^([abcdefghjklmnpqrsuvwABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/;
    const nieRegex = /^[XYZxyz][0-9]{7}[A-Za-z]$/;
    const passportRegex = /^[a-zA-Z][0-9A-Za-z]{2,19}$/;

    switch (type) {
      case "NIF/CIF":
        isValid = nifRegex.test(value) || cifRegex.test(value);
        errorMessage = isValid ? "" : "Formato de NIF/CIF incorrecto";
        break;
      case "NIE":
        isValid = nieRegex.test(value);
        errorMessage = isValid ? "" : "Formato de NIE incorrecto";
        break;
      case "Pasaporte":
        isValid = passportRegex.test(value);
        errorMessage = isValid ? "" : "Formato de Pasaporte incorrecto";
        break;
      case "Otro":
        isValid = true;
        break;
      default:
        isValid = false;
        errorMessage = "Tipo de documento desconocido";
    }
    return { isValid, errorMessage };
  };

  const handleDocumentTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value as string;
    setDocumentType(newType);

    const currentDocumentValue = formData.document;

    if (newType === "") {
      setFormData({ ...formData, document: "" });
      setDocumentError("");
      setIsButtonDisabled(true);
    } else {
      const { isValid, errorMessage } = validateDocument(
        newType,
        currentDocumentValue,
      );
      setDocumentError(errorMessage);
      setIsButtonDisabled(!isValid);

      if (currentDocumentValue && newType !== "Otro") {
        setDocumentError(errorMessage);
      } else {
        setDocumentError("");
        setDocumentInputEnabled(false);
      }
    }
  };

  const handleDocumentChange = (
    event: React.FocusEvent<HTMLInputElement>,
    fieldId: keyof ChangeOwnershipData,
  ) => {
    const { value } = event.target;
    const { isValid, errorMessage } = validateDocument(documentType, value);

    if (value !== "") {
      setDocumentError(errorMessage);
      setIsButtonDisabled(!isValid);
    } else {
      setDocumentError("");
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldId: keyof ChangeOwnershipData,
  ) => {
    const updatedFormData = { ...formData };
    updatedFormData[fieldId] = event.target.value;
    setFormData(updatedFormData);
  };

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldId: keyof ChangeOwnershipData,
  ) => {
    const updatedFormData = { ...formData };
    updatedFormData[fieldId] = event.target.value;
    setFormData(updatedFormData);
  };

  const [selectedOwnershipChangeFile, setSelectedOwnershipChangeFile] =
    useState<File | null>(null);
  const [selectedSEPAFile, setSelectedSEPAFile] = useState<File | null>(null);
  const [selectedNewOwnerDNIFile, setSelectedNewOwnerDNIFile] =
    useState<File | null>(null);
  const [freelanceFile, setFreelanceFile] = useState<File | null>(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    const isFieldsFilled =
      formData.document !== "" &&
      formData.social !== "" &&
      formData.fiscal !== "" &&
      freelanceFile;

    const isCambioTitularidadUploaded = !!selectedOwnershipChangeFile;
    const isMandatoSEPAUploaded = !!selectedSEPAFile;
    const isDNINuevoTitularUploaded = !!selectedNewOwnerDNIFile;

    const areFilesUploaded =
      isCambioTitularidadUploaded &&
      isMandatoSEPAUploaded &&
      isDNINuevoTitularUploaded;

    setChangeText(
      `El usuario desea realizar un cambio de titularidad. Documento identificativo del nuevo titular: ${documentType}, número ${
        formData.document
      }. Razón social del nuevo titular: ${
        formData.social
      }. Dirección fiscal del nuevo titular: ${
        formData.fiscal
      }. Se adjuntan los siguientes anexos: Cambio de titularidad, Mandato SEPA y DNI del nuevo titular. ${
        freelanceFile != null
          ? "Se incluye también el Justificante de Autónomo a la petición."
          : ""
      }`,
    );

    const checkIsButtonDisabled = !(isFieldsFilled && areFilesUploaded);
    setIsButtonDisabled(checkIsButtonDisabled);

    if (ownerData) {
      const { isAdministrator, idAdministrator, idClient } = ownerData;
      setUpdateOwnershipData((prevUpdateOwnershipData) => ({
        ...prevUpdateOwnershipData,
        idClient: isAdministrator ? idAdministrator : idClient,
        isAdministrator: isAdministrator.toString(),
      }));
    }

    if (idCups.length && ownerData) {
      setUpdateOwnershipData((prevUpdateOwnershipData) => ({
        ...prevUpdateOwnershipData,
        cups: [formatOwnerDataCup(ownerDataCup)],
      }));
    }
  }, [
    documentType,
    formData,
    freelanceFile,
    idCups,
    ownerData,
    ownerDataCup,
    selectedNewOwnerDNIFile,
    selectedOwnershipChangeFile,
    selectedSEPAFile,
  ]);

  const handleSubmit = async () => {
    setStep(1);
    setIsLoading(true);
    setDocumentImportErrors({});
    let envirorment = 0;
    let subtype = 0;
    switch (updateOwnershipData.cups[0].envirorment) {
      case "Luz":
        envirorment = MANAGEMENTS_ENVIRONMENT_LIGHT;
        subtype = MANAGEMENTS_SUBTYPE_OWNERSHIP_LIGHT;
        break;
      case "Gas":
        envirorment = MANAGEMENTS_ENVIRONMENT_GAS;
        subtype = MANAGEMENTS_SUBTYPE_OWNERSHIP_GAS;
        break;
      default:
        envirorment = 0;
    }

    const selectedCups = getSelectedCups(
      updateOwnershipData.cups,
      listCupsCifFiltered,
    );

    const contractCodes = selectedCups.map(
      ({ cupsContractCode }) => +cupsContractCode,
    );

    const hasValidContractCodes = contractCodes.some(
      (code) => !isNaN(code) && code !== null && code !== undefined,
    );

    const requestBody: PostCallCenterRequestBody = {
      TextoIncidencia: changeText,
      CodigosContratos: hasValidContractCodes ? contractCodes : null,
      IdsContratos: selectedCups.map(({ cupsContractId }) => +cupsContractId),
      IdTipoRegistro: envirorment,
      IdSubTipoReclamacion: subtype,
      idUsuarioDestino: getRecipientId(envirorment, subtype),
      ClaveMedio: isAgent ? "09" : "12",
    };

    if (userData) {
      try {
        const responseArray = await postCallCenter(requestBody, userData.token);

        if (Array.isArray(responseArray) && responseArray.length > 0) {
          if (responseArray.some(({ IdCallCenter }) => IdCallCenter === 0)) {
            throw new Error("One or more IdCallCenter values are 0");
          }

          const successfulRequests: ManagementsSuccessfulRequests[] = [];
          const failedImportFilesByReference: Record<string, string[]> = {};

          for (const contract of responseArray) {
            const { IdCallCenter, IdCarpetaIncidencias, Cups, EntornoBD } =
              contract;
            const address = ownerData?.cups.find(
              (item) => item.codeCups === Cups,
            )?.address;
            const failedFileNames: string[] = [];

            for (const file of [
              selectedOwnershipChangeFile,
              selectedSEPAFile,
              selectedNewOwnerDNIFile,
              freelanceFile,
            ]) {
              if (file && IdCarpetaIncidencias !== 0) {
                try {
                  await postImportDocument(
                    IdCallCenter,
                    IdCarpetaIncidencias,
                    file,
                    userData.token,
                  );
                } catch {
                  failedFileNames.push(file.name);
                }
              }
            }

            if (failedFileNames.length > 0) {
              failedImportFilesByReference[IdCallCenter.toString()] =
                failedFileNames;
            }

            successfulRequests.push({
              registrarionDate: formatDate(new Date()),
              refferenceNumber: IdCallCenter.toString(),
              cupsInfo: {
                cupsCode: Cups,
                address: address ?? "",
                environment: EntornoBD,
              },
            });
          }

          setDocumentImportErrors(failedImportFilesByReference);

          if (successfulRequests.length > 0) {
            setRequestState(MANAGEMENTS_STATE_PENDING);
            setRequestStateData(
              getManagementsRequestTypeData(
                MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
                MANAGEMENTS_STATE_PENDING,
                isAgent ?? false,
              ),
            );

            setManagementRequestInfo([...successfulRequests]);
          } else {
            setRequestState(MANAGEMENTS_STATE_ERROR);
            setRequestStateData(
              getManagementsRequestTypeData(
                MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
                MANAGEMENTS_STATE_ERROR,
                isAgent ?? false,
              ),
            );
          }
        } else {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false,
            ),
          );
        }
      } catch (error) {
        console.error("Error:", error);
        setRequestState(MANAGEMENTS_STATE_ERROR);
        setRequestStateData(
          getManagementsRequestTypeData(
            MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
            MANAGEMENTS_STATE_ERROR,
            isAgent ?? false,
          ),
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      component="section"
      className="management-ownership-change"
      paddingBottom={{ md: "5rem" }}
    >
      <ManagementHeader
        title={"Cambio de titular"}
        step={step}
        showStepper={true}
      />
      {step === -1 && (
        <Stack
          component="article"
          direction="column"
          justifyContent="center"
          alignItems="center"
          paddingTop={5}
          paddingBottom={10}
          bgcolor={{ md: theme.palette.grey[50] }}
        >
          <CupsCifSelector
            withContainer
            setUpdateCups={(selectedCups) => {
              setUpdateOwnershipData((prevUpdateOwnershipData) => ({
                ...prevUpdateOwnershipData,
                cups: selectedCups,
              }));
              setStep(0);
              window.scrollTo({ top: 0, behavior: "auto" });
            }}
            selectedCups={selectedCups}
            setSelectedCups={setSelectedCups}
            title={"Gestiones.¿Dónde quieres aplicar el cambio?"}
            description={"Gestiones.El cambio se solicitará"}
          />
        </Stack>
      )}
      {step === 0 && (
        <Grid container justifyContent="center">
          <Grid item md={8} lg={7} flex={1}>
            <Stack
              bgcolor={{ md: "#fff" }}
              borderRadius="12px"
              boxShadow={{ md: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)" }}
              padding="3rem"
              marginY="2.5rem"
              gap={{ xs: "2rem", md: "3.5rem" }}
            >
              <Stack gap={"1.5rem"}>
                <Typography component="h2" variant="h3" paddingBottom="0.5rem">
                  {t("Gestiones.Nuevo titular")}
                </Typography>

                <FormControl
                  fullWidth
                  required
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    width: { xs: "100%", md: "50%" },
                  }}
                >
                  <FormLabel sx={{ fontWeight: 600 }}>
                    {t("Gestiones.Documento")}
                    <span style={{ color: "red" }}> *</span>
                  </FormLabel>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <FormControl fullWidth>
                      <Select
                        id="document-type-select"
                        value={documentType}
                        onChange={handleDocumentTypeChange}
                        displayEmpty
                        sx={{ width: "100%" }}
                      >
                        <MenuItem value="" disabled>
                          {t("Gestiones.Selecciona un tipo de documento")}
                        </MenuItem>
                        {documentTypeOptions.map((option) => (
                          <MenuItem key={option.label} value={option.label}>
                            {t(`Gestiones.${option.label}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <OutlinedInput
                        name="document"
                        id="document"
                        data-testid="document-owner"
                        type="text"
                        error={!!documentError}
                        value={formData.document}
                        onChange={(e) =>
                          handleInputChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            "document",
                          )
                        }
                        onBlur={(e) =>
                          handleDocumentChange(
                            e as React.FocusEvent<HTMLInputElement>,
                            "document",
                          )
                        }
                        sx={{ maxWidth: { md: "100%" }, width: "100%" }}
                        disabled={documentInputEnabled}
                      />
                      {documentError && (
                        <FormHelperText
                          error
                          sx={{
                            marginLeft: 0,
                            marginTop: 1,
                            color: theme.palette.error.main,
                          }}
                        >
                          <span className="t-icon-CGU" />{" "}
                          {t(`Gestiones.${documentError}`)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </FormControl>

                {updateOwnershipFields.map(({ id, label, inputMode }) => (
                  <FormControl
                    required
                    key={`${label}`}
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      width: { xs: "100%", md: "50%" },
                    }}
                  >
                    <FormLabel sx={{ fontWeight: 600 }} htmlFor={id}>
                      {t(`Gestiones.${label}`)}
                      <span style={{ color: "red" }}> *</span>
                    </FormLabel>
                    <Box
                      component="div"
                      display="flex"
                      flexDirection="row"
                      flex={1}
                      width={"100%"}
                    >
                      <OutlinedInput
                        name={id}
                        id={id}
                        type={inputMode}
                        sx={{
                          maxWidth: { md: "100%" },
                          width: "100%",
                        }}
                        value={formData[id as keyof ChangeOwnershipData]}
                        onChange={(e) =>
                          handleFormChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            id as keyof ChangeOwnershipData,
                          )
                        }
                      />
                    </Box>
                  </FormControl>
                ))}
              </Stack>

              <Stack>
                <Typography component="h2" variant="h3" paddingBottom={"1rem"}>
                  {t("Gestiones.Anexos")}
                </Typography>
                <Typography paddingBottom={"2rem"}>
                  {t(
                    "Gestiones.Aceptamos formato pdf, png y jpg. Máx 4 MB para todos los archivos.",
                  )}
                </Typography>
                <Stack gap={"1.5rem"} paddingBottom={"2rem"}>
                  <ManagementUploadFile
                    title={t("Gestiones.Cambio de titularidad")}
                    isRequired={true}
                    downloadButtonText={t(
                      "Gestiones.Descargar cambio de titularidad",
                    )}
                    downloadDocumentType="02"
                    inputFileInstructions={false}
                    onFileUpload={(id, file) => {
                      setSelectedOwnershipChangeFile(file);
                    }}
                    onFileRemove={(id) => {
                      setSelectedOwnershipChangeFile(null);
                    }}
                  />
                  <ManagementUploadFile
                    title={t("Gestiones.Mandato SEPA")}
                    isRequired={true}
                    downloadDocumentType="01"
                    inputFileInstructions={false}
                    downloadButtonText={t("Gestiones.Descargar mandato SEPA")}
                    onFileUpload={(id, file) => {
                      setSelectedSEPAFile(file);
                    }}
                    onFileRemove={(id) => {
                      setSelectedSEPAFile(null);
                    }}
                  />
                  <ManagementUploadFile
                    title={t("Gestiones.DNI del nuevo titular")}
                    isRequired={true}
                    inputFileInstructions={false}
                    downloadButtonText={t("Gestiones.Cambio de titularidad")}
                    onFileUpload={(id, file) => {
                      setSelectedNewOwnerDNIFile(file);
                    }}
                    onFileRemove={(id) => {
                      setSelectedNewOwnerDNIFile(null);
                    }}
                  />
                  <ManagementUploadFile
                    title={t("Gestiones.Justificante de autónomo")}
                    isRequired={true}
                    inputFileInstructions={false}
                    optionalCustomMessage={t("Gestiones.Solo para autónomos")}
                    onFileUpload={(id, file) => {
                      setFreelanceFile(file);
                    }}
                    onFileRemove={(id) => {
                      setFreelanceFile(null);
                    }}
                  />
                </Stack>

                <FormControl
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    gap: "1.5rem",
                    marginY: "1.5rem",
                  }}
                >
                  <ButtonElement
                    onClick={() => handleSubmit()}
                    disabled={isButtonDisabled}
                  >
                    {t("Gestiones.Solicitar cambio de titularidad")}
                  </ButtonElement>
                  <ButtonElement type="button" secondary onClick={openModal}>
                    {t("Cancelar")}
                  </ButtonElement>
                </FormControl>
              </Stack>
            </Stack>
            <ManagementsCancelProcess
              open={isModalOpen}
              handleClose={closeModal}
            />
          </Grid>
        </Grid>
      )}
      {step === 1 && (
        <Box width={"100%"}>
          {managementRequestInfo?.map((request) => (
            <ManagementsRequestState
              key={request.refferenceNumber}
              showManagementRequestInfo
              isLoading={isLoading}
              state={requestState}
              managementRequestInfo={request}
              title={requestStateData.title}
              description={requestStateData.description}
              descriptionExtra={requestStateData.descriptionExtra}
              cupsInfo={request.cupsInfo}
              documentImportError={
                !!documentImportErrors[request.refferenceNumber]?.length
              }
              documentImportErrorFiles={
                documentImportErrors[request.refferenceNumber] ?? []
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ManagementOwnershipChange;
